// src/live/components/LiveToast.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Radio } from 'lucide-react';

export interface LiveToastData {
  id: string;
  streamId: string;
  creatorName: string;
  creatorAvatar?: string;
  streamTitle: string;
  source: string;
}

interface LiveToastProps {
  toast: LiveToastData;
  onDismiss: (id: string) => void;
  autoDismissMs?: number;
}

export default function LiveToast({ toast, onDismiss, autoDismissMs = 8000 }: LiveToastProps) {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, autoDismissMs);

    return () => clearTimeout(timer);
  }, [autoDismissMs]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  const handleWatch = () => {
    if (toast.source === 'event') {
      navigate(`/event/${toast.streamId}`);
    } else {
      navigate(`/live/${toast.streamId}`);
    }
    handleDismiss();
  };

  return (
    <div
      className={`bg-zinc-800 border border-zinc-700 rounded-lg p-4 shadow-xl max-w-sm transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {toast.creatorAvatar ? (
            <img
              src={toast.creatorAvatar}
              alt={toast.creatorName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
              <Radio className="w-5 h-5 text-black" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm">
            {toast.creatorName} is live!
          </p>
          <p className="text-zinc-400 text-xs truncate mt-0.5">
            {toast.streamTitle}
          </p>
          <button
            onClick={handleWatch}
            className="mt-2 text-yellow-400 text-sm font-medium hover:text-yellow-300"
          >
            Watch now →
          </button>
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-zinc-500 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
